import { format } from 'date-fns';

/**
 * Сборка query-параметров журнала звонков.
 *
 * Вынесена из компонента, потому что через неё ходят и загрузка списка, и Excel-экспорт,
 * и потому что тестировать её иначе нечем: React Testing Library в проекте нет.
 *
 * Легаси-разрезы (`primary_intent`, `service_area`, `booking_outcome`, `quality_form`,
 * `call_classification_type`, `alternative_offered`) отсюда ушли вместе со своими селектами:
 * в режиме нод соответствующие колонки `calls` не заполняются, и фильтр по ним всегда
 * возвращал пустоту. На сервере параметры остались — их ждёт откат и старая история.
 */
export const ALL = 'all';

const clean = (value) => (value && value !== ALL ? value : undefined);

const nonEmpty = (list) => (list?.length > 0 ? list : undefined);

const formatDate = (value) => (value ? format(value, 'yyyy-MM-dd') : undefined);

const toList = (value) => [
  ...new Set((Array.isArray(value) ? value : [value]).filter((item) => item && item !== ALL))
];

/**
 * Срез раздела «Сотрудники», пришедший с переходом: списки сотрудников и отделов.
 *
 * Живёт отдельно от селектов журнала и снимается чипами. В селекты его не положить: у них
 * одиночный выбор, а их списки — звонки за период и направление (filter-options), тогда как
 * «Сотрудники» выбирают из справочника. Значение, которого в списке нет, эффекты журнала
 * сбрасывают на «все», и журнал после перехода оказывался шире карточки.
 */
export const EMPTY_EMPLOYEE_SCOPE = Object.freeze({ employeeIds: [], departments: [] });

export const readEmployeeScope = (filters) => {
  const employeeIds = toList(filters?.employee_ids);
  const departments = toList(filters?.departments);

  return employeeIds.length > 0 || departments.length > 0
    ? { employeeIds, departments }
    : EMPTY_EMPLOYEE_SCOPE;
};

// Длинный выбор в чипе обрезается: десяток имён растянул бы шапку журнала на полэкрана.
const SCOPE_LABELS_SHOWN = 3;

const formatScopeLabel = (title, labels, total) => {
  const shown = labels.slice(0, SCOPE_LABELS_SHOWN);
  if (shown.length === 0) {
    return `${title}: ${total}`;
  }

  const rest = total - shown.length;
  return `${title}: ${shown.join(', ')}${rest > 0 ? ` и ещё ${rest}` : ''}`;
};

export const describeScopeDepartments = (departments) =>
  formatScopeLabel(departments.length === 1 ? 'Отдел' : 'Отделы', departments, departments.length);

/**
 * Имена — из справочника и списка журнала за период. Сотрудника, которого нет ни там, ни там,
 * не показываем GUID'ом: он уходит в «и ещё N».
 */
export const describeScopeEmployees = (employeeIds, nameById) =>
  formatScopeLabel(
    employeeIds.length === 1 ? 'Сотрудник' : 'Сотрудники',
    employeeIds.map((id) => nameById?.get(id)).filter(Boolean),
    employeeIds.length
  );

export const buildCallFilterParams = ({
  department,
  employeeId,
  employeeScope,
  callType,
  search,
  dateRange,
  nodeFilter,
  criterionFilter
} = {}) => ({
  department: clean(department),
  employee_id: clean(employeeId),
  // Срез «Сотрудников» пересекается с селектами на сервере, а не подменяет их.
  employee_ids: nonEmpty(employeeScope?.employeeIds),
  departments: nonEmpty(employeeScope?.departments),
  call_type: clean(callType),
  // Критерий прилетает с вкладки «Сотрудники» (график запрещённых фраз) и своего селекта
  // не имеет — снимается чипом.
  criterion: criterionFilter?.criterion ?? undefined,
  criterion_max_score: criterionFilter?.criterion != null ? criterionFilter.maxScore : undefined,
  node_filter: nodeFilter || undefined,
  search: search || undefined,
  date_from: formatDate(dateRange?.from),
  date_to: formatDate(dateRange?.to)
});
